import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import {
  resolvePublicNav,
  type MediaKitSectionType,
  type PublicNavItem,
} from "./layout";
import {
  AboutSection,
  AudienceSection,
  CampaignsSection,
  CollaborationSection,
  ContactSection,
  HeroSection,
  PerformanceSection,
  PhonePreviewSection,
  VisualWorldSection,
} from "./sections";
import type { MediaKitContent } from "./types";
import { useMediaKitAnalytics } from "./useMediaKitAnalytics";
import { useMediaKitContent } from "./useMediaKitContent";
// lipis/flag-icons — country flags in audience stats
import "flag-icons/css/flag-icons.min.css";
import "./media-kit.css";

const MOBILE_MQ = "(max-width: 840px)";

const SECTION_RENDERERS: Record<
  MediaKitSectionType,
  (content: MediaKitContent) => JSX.Element | null
> = {
  hero: (c) => <HeroSection content={c} />,
  about: (c) => <AboutSection content={c} />,
  performance: (c) => <PerformanceSection content={c} />,
  audience: (c) => <AudienceSection content={c} />,
  visualWorld: (c) => <VisualWorldSection content={c} />,
  phonePreview: (c) => <PhonePreviewSection content={c} />,
  campaigns: (c) => <CampaignsSection content={c} />,
  collaboration: (c) => <CollaborationSection content={c} />,
  contact: (c) => <ContactSection content={c} />,
};

function Navigation({
  items,
  activeId,
  onNavigate,
  hidden,
  onPointerEngage,
}: {
  items: PublicNavItem[];
  activeId: string;
  onNavigate: (id: string) => void;
  hidden: boolean;
  onPointerEngage: () => void;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || hidden) return;
    const active = scroller.querySelector<HTMLElement>(`[data-nav-id="${activeId}"]`);
    if (!active) return;
    const left =
      active.offsetLeft - (scroller.clientWidth - active.offsetWidth) / 2;
    const max = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const next = Math.max(0, Math.min(left, max));
    try {
      scroller.scrollTo({ left: next, behavior: "smooth" });
    } catch {
      scroller.scrollLeft = next;
    }
  }, [activeId, hidden, items]);

  if (items.length <= 1) return null;

  return (
    <div
      className={`mk-nav-shell${hidden ? " mk-nav-shell--hidden" : ""}`}
      data-nav-hidden={hidden ? "true" : "false"}
      onPointerDown={onPointerEngage}
      onTouchStart={onPointerEngage}
    >
      <div className="mk-nav-fade mk-nav-fade--left" aria-hidden="true" />
      <div className="mk-nav-fade mk-nav-fade--right" aria-hidden="true" />
      <div
        ref={scrollerRef}
        className="mk-navigation"
        role="tablist"
        aria-label="Secciones del media kit"
        {...(hidden ? ({ inert: true } as Record<string, unknown>) : {})}
      >
        {items.map((item) => {
          const selected = activeId === item.id;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              id={`mk-tab-${item.id}`}
              data-nav-id={item.id}
              data-active={selected ? "true" : "false"}
              className={selected ? "mk-nav-active" : undefined}
              aria-selected={selected}
              aria-controls={item.id}
              tabIndex={hidden ? -1 : selected ? 0 : -1}
              onClick={() => onNavigate(item.id)}
            >
              {item.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function MediaKitPage() {
  const { slug: routeSlug } = useParams<{ slug?: string }>();
  // /mediakit → general (undefined); /mediakit/fashion → slug
  const kitSlug = routeSlug?.trim() || null;
  const {
    content,
    loading,
    error,
    source,
    kitTitle,
    isGeneral,
  } = useMediaKitContent(kitSlug);

  const navItems = useMemo(
    () => resolvePublicNav(content.layout),
    [content.layout],
  );

  const rootRef = useRef<HTMLDivElement>(null);
  const lastScrollY = useRef(0);
  const navLockUntil = useRef(0);
  const programmaticTargetY = useRef<number | null>(null);
  const [activeId, setActiveId] = useState<string>("portada");
  const [navHidden, setNavHidden] = useState(false);

  useSEO("mediakit");

  const { trackNavJump } = useMediaKitAnalytics({
    content,
    loading,
    error,
    contentSource: source,
    routeSlug: kitSlug,
    kitTitle,
    isGeneralKit: isGeneral,
    navItems,
    activeId,
    rootRef,
  });

  useEffect(() => {
    if (navItems.length && !navItems.some((n) => n.id === activeId)) {
      setActiveId(navItems[0].id);
    }
  }, [navItems, activeId]);

  useEffect(() => {
    if (error && import.meta.env.DEV) {
      console.warn("[mediakit] using fallback content:", error);
    }
  }, [error]);

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    root.classList.add("media-kit-active");
    body.classList.add("media-kit-active");

    const prev = {
      htmlBg: root.style.backgroundColor,
      bodyBg: body.style.backgroundColor,
      htmlOverflow: root.style.overflow,
      bodyOverflow: body.style.overflow,
      overscroll: body.style.overscrollBehavior,
      htmlOverscroll: root.style.overscrollBehavior,
    };

    const apply = () => {
      const mobile = window.matchMedia(MOBILE_MQ).matches;
      root.style.backgroundColor = "#000000";
      body.style.backgroundColor = "#000000";
      body.style.overscrollBehavior = "none";
      root.style.overscrollBehavior = "none";
      if (mobile) {
        root.style.overflow = "hidden";
        body.style.overflow = "hidden";
      } else {
        root.style.overflow = prev.htmlOverflow;
        body.style.overflow = prev.bodyOverflow;
      }
    };

    apply();
    const mq = window.matchMedia(MOBILE_MQ);
    mq.addEventListener("change", apply);

    return () => {
      mq.removeEventListener("change", apply);
      root.classList.remove("media-kit-active");
      body.classList.remove("media-kit-active");
      root.style.backgroundColor = prev.htmlBg;
      body.style.backgroundColor = prev.bodyBg;
      body.style.overscrollBehavior = prev.overscroll;
      root.style.overscrollBehavior = prev.htmlOverscroll;
      root.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
    };
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || loading) return;

    const sections = navItems
      .map((n) => root.querySelector<HTMLElement>(`#${n.id}`))
      .filter(Boolean) as HTMLElement[];
    if (sections.length === 0) return;

    const mq = window.matchMedia(MOBILE_MQ);
    let observer: IntersectionObserver | null = null;
    let raf = 0;

    const pickActive = () => {
      const scrollRoot = mq.matches ? root : null;
      const rootTop = scrollRoot ? scrollRoot.getBoundingClientRect().top : 0;
      const rootH = scrollRoot ? scrollRoot.clientHeight : window.innerHeight;
      const anchor = rootTop + rootH * 0.28;

      let bestId = sections[0]?.id ?? navItems[0]?.id ?? "portada";
      for (const s of sections) {
        if (s.getBoundingClientRect().top <= anchor + 1) {
          bestId = s.id;
        }
      }
      setActiveId((prev) => (prev === bestId ? prev : bestId));
    };

    const schedulePick = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        pickActive();
      });
    };

    const bind = () => {
      observer?.disconnect();
      const scrollRoot = mq.matches ? root : null;
      observer = new IntersectionObserver(schedulePick, {
        root: scrollRoot,
        rootMargin: "0px",
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
      });
      sections.forEach((s) => observer!.observe(s));

      const scrollTarget: HTMLElement | Window = mq.matches ? root : window;
      scrollTarget.addEventListener("scroll", schedulePick, { passive: true });
      (bind as { _scrollTarget?: HTMLElement | Window })._scrollTarget = scrollTarget;

      pickActive();
    };

    const unbindScroll = () => {
      const t = (bind as { _scrollTarget?: HTMLElement | Window })._scrollTarget;
      t?.removeEventListener("scroll", schedulePick);
    };

    bind();
    const onMq = () => {
      unbindScroll();
      bind();
    };
    mq.addEventListener("change", onMq);

    return () => {
      mq.removeEventListener("change", onMq);
      unbindScroll();
      observer?.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [loading, content, navItems]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const reducedMq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (reducedMq.matches) {
      setNavHidden(false);
      return;
    }

    const mq = window.matchMedia(MOBILE_MQ);
    let target: HTMLElement | Window = mq.matches ? root : window;

    const readY = () => (mq.matches ? root.scrollTop : window.scrollY);

    const navHasFocus = () => {
      const active = document.activeElement;
      const nav = root.querySelector(".mk-navigation");
      return !!(active && nav && nav.contains(active));
    };

    const onScroll = () => {
      const y = readY();

      const targetY = programmaticTargetY.current;
      if (targetY != null) {
        lastScrollY.current = y;
        if (Math.abs(y - targetY) < 12 || performance.now() > navLockUntil.current) {
          programmaticTargetY.current = null;
        }
        setNavHidden(false);
        return;
      }

      if (performance.now() < navLockUntil.current) {
        lastScrollY.current = y;
        setNavHidden(false);
        return;
      }

      if (navHasFocus()) {
        lastScrollY.current = y;
        setNavHidden(false);
        return;
      }

      const delta = y - lastScrollY.current;
      lastScrollY.current = y;

      if (y < 48) {
        setNavHidden(false);
        return;
      }
      if (delta > 10) setNavHidden(true);
      else if (delta < -8) setNavHidden(false);
    };

    const bind = () => {
      target.removeEventListener("scroll", onScroll);
      target = mq.matches ? root : window;
      lastScrollY.current = readY();
      target.addEventListener("scroll", onScroll, { passive: true });
    };

    bind();
    mq.addEventListener("change", bind);

    const onReducedChange = () => {
      if (reducedMq.matches) setNavHidden(false);
    };
    reducedMq.addEventListener("change", onReducedChange);

    return () => {
      mq.removeEventListener("change", bind);
      reducedMq.removeEventListener("change", onReducedChange);
      target.removeEventListener("scroll", onScroll);
    };
  }, [loading]);

  const lockNavVisible = useCallback((ms = 1200) => {
    navLockUntil.current = performance.now() + ms;
    setNavHidden(false);
  }, []);

  const onNavigate = useCallback(
    (id: string, method: "button" | "hash" = "button") => {
      const root = rootRef.current;
      const el = root?.querySelector<HTMLElement>(`#${id}`);
      if (!el) return;

      const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const isMobile = window.matchMedia(MOBILE_MQ).matches;

      trackNavJump(id, method);
      setActiveId(id);
      lockNavVisible(prefersReduced ? 200 : 2500);

      if (isMobile && root) {
        const top =
          el.getBoundingClientRect().top -
          root.getBoundingClientRect().top +
          root.scrollTop;
        const targetTop = Math.max(0, top);
        programmaticTargetY.current = targetTop;
        root.scrollTo({
          top: targetTop,
          behavior: prefersReduced ? "auto" : "smooth",
        });

        const clearProg = () => {
          programmaticTargetY.current = null;
          setNavHidden(false);
        };
        const onEnd = () => {
          root.removeEventListener("scrollend", onEnd);
          clearProg();
        };
        if ("onscrollend" in window) {
          root.addEventListener("scrollend", onEnd, { once: true });
        }
        if (prefersReduced) {
          clearProg();
        }
      } else {
        programmaticTargetY.current = null;
        el.scrollIntoView({
          behavior: prefersReduced ? "auto" : "smooth",
          block: "start",
        });
      }

      try {
        history.replaceState(null, "", `#${id}`);
      } catch {
        /* ignore */
      }
    },
    [lockNavVisible, trackNavJump],
  );

  useEffect(() => {
    if (loading) return;
    const hash = window.location.hash.replace("#", "");
    if (hash && navItems.some((n) => n.id === hash)) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => onNavigate(hash, "hash"));
      });
    }
  }, [loading, onNavigate, navItems]);

  return (
    <div
      ref={rootRef}
      className="media-kit-root"
      data-loading={loading ? "true" : "false"}
      data-active-section={activeId}
      data-nav-hidden={navHidden ? "true" : "false"}
      data-kit-slug={content.slug || kitSlug || "general"}
    >
      <Navigation
        items={navItems}
        activeId={activeId}
        onNavigate={onNavigate}
        hidden={navHidden}
        onPointerEngage={() => lockNavVisible(800)}
      />
      <main className="mk-media-kit" aria-busy={loading}>
        {navItems.map((item) => (
          <div key={item.type}>{SECTION_RENDERERS[item.type](content)}</div>
        ))}
      </main>
    </div>
  );
}
